// Push routing in the browser (api-phase2.md "Offers" and "Parking and raise"), mounted once in the shell
// so it works on every screen:
//   - offer card: severity, site, analytic, camera, alarm count, countdown to expiresAt;
//     Accept (Enter) and Decline (Esc, optional reason); a soft chime unless muted.
//   - pre-staging: while the offer shows, the alarm camera (alarm clip) and its nearest cameras (live) are
//     opened muted in an off-screen container, so their streams are warm when Accept opens the event.
//     These are real views on the watch tracker, marked prestage, never counted as watched, and they end
//     as `closed` when the offer goes away (accept, decline, expiry or withdrawal).
//   - offer_withdrawn clears the card; GET /api/offers/mine runs on load and after every reconnect.
//   - raise: a chime; the event shows in the tabs or the queue (no pop-up, the user 2026-10-28).

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, errorText, readOffer, type Offer, type RaiseMessage } from '../api.ts';
import { AuthImg } from '../components/AuthMedia.tsx';
import { Icon } from '../components/Icon.tsx';
import { CameraFeed } from '../components/VideoPlayer.tsx';
import { QueueAiLine } from '../console/Agent.tsx';
import { Since } from '../console/Ticks.tsx';
import { useToaster } from '../console/Toasts.tsx';
import { camShort, human, isTyping, ms, plural, SEV_CLS, SEV_LABEL } from '../console/util.ts';
import { useNow } from '../hooks.ts';
import { href, navigate } from '../router.ts';
import { useStreamEvent, useStreamStatus } from '../stream.tsx';
import { emitLive } from './bus.ts';
import { MuteToggle, usePresence } from './presence.tsx';
import { chime } from './sound.ts';

const PRE_ROLL_MS = 10_000;
/** Keep an expired card this long waiting for offer_withdrawn, then drop it and ask the server. */
const EXPIRY_GRACE_MS = 2500;
const DECLINE_REASONS = ['On a call', 'Handling a critical event', 'Missing skill or language', 'Stepping away'];


/* ---------- pre-staging ---------- */

function Prestage({ offer }: { offer: Offer }) {
  const e = offer.event;
  const first = ms(e.firstAlarmAt);
  const at = first !== null ? new Date(first - PRE_ROLL_MS).toISOString() : null;
  const cams = offer.prestage.cameraIds.slice(0, 4);
  if (!cams.length) return null;
  return (
    <div className="prestage" aria-hidden="true" inert>
      {cams.map((id, i) => (
        <div key={id} className="prestage-cell">
          <CameraFeed
            cameraId={id}
            cameraName={i === 0 ? (e.primaryCameraName ?? id) : id}
            // The alarm camera opens the way the workspace will (the alarm clip); the nearest cameras open live.
            at={i === 0 ? at : null}
            alarmClip={i === 0 && !!at}
            eventId={null}
            paused={false}
            prestage
          />
        </div>
      ))}
    </div>
  );
}

/* ---------- offer card ---------- */

function OfferCard({
  offer,
  busy,
  error,
  onAccept,
  onDecline,
}: {
  offer: Offer;
  busy: 'accept' | 'decline' | null;
  error: unknown;
  onAccept: () => void;
  onDecline: (reason: string | undefined) => void;
}) {
  const e = offer.event;
  const now = useNow(250);
  const exp = ms(offer.expiresAt) ?? now;
  // The card's own clock: total time from when it first showed, so the bar starts full.
  const shownAt = useRef(now);
  const total = Math.max(1000, exp - shownAt.current);
  const left = Math.max(0, exp - now);
  const frac = Math.min(1, left / total);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const cardRef = useRef<HTMLDivElement | null>(null);
  const reasonRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (declining) reasonRef.current?.focus();
  }, [declining]);

  // Enter / Esc from anywhere, except while typing in another field (a drawer note, a search box).
  const keyRef = useRef<(k: KeyboardEvent) => void>(() => undefined);
  keyRef.current = (k: KeyboardEvent) => {
    if (k.ctrlKey || k.metaKey || k.altKey || busy) return;
    const t = k.target;
    const inside = t instanceof Node && !!cardRef.current?.contains(t);
    if (isTyping(t) && !inside) return;
    if (k.key === 'Enter') {
      // A focused button inside the card handles its own Enter.
      if (inside && t instanceof HTMLButtonElement) return;
      k.preventDefault();
      k.stopPropagation();
      if (declining) onDecline(reason.trim() || undefined);
      else if (left > 0) onAccept();
    } else if (k.key === 'Escape') {
      k.preventDefault();
      k.stopPropagation();
      setDeclining((d) => !d);
    }
  };
  useEffect(() => {
    const h = (k: KeyboardEvent) => keyRef.current(k);
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, []);

  const secs = Math.ceil(left / 1000);
  const urgent = left <= 5000;
  return (
    <div className="offer-wrap" role="presentation">
      <div
        ref={cardRef}
        className={`offer s-${SEV_CLS[e.severity]}${urgent ? ' urgent' : ''}`}
        role="alertdialog"
        aria-modal="false"
        aria-labelledby="offer-title"
        aria-describedby="offer-desc"
      >
        <div className="offer-top">
          <span className="lbl offer-k">
            <Icon name="inbox" />
            {e.state === 'transferred' || e.transferNote ? 'Sent to you' : e.raised ? 'Raised event' : 'New event offered'}
          </span>
          <span className="sp" />
          <span className={`offer-count mono${urgent ? ' late' : ''}`} aria-live="off" title="Time left to accept">
            {left > 0 ? `0:${String(secs).padStart(2, '0')}` : 'expired'}
          </span>
          <MuteToggle className="sm" />
        </div>
        <div className="offer-bar" aria-hidden="true">
          <i style={{ transform: `scaleX(${frac})` }} />
        </div>
        <div className="offer-main">
          <div className="offer-thumb">
            {e.snapshotUrl ? <AuthImg url={e.snapshotUrl} alt="" fallback="" /> : <span className="qph"><Icon name="cam" /></span>}
          </div>
          <div className="offer-txt">
            <div className="row-wrap">
              <span className="sev big">{SEV_LABEL[e.severity]}</span>
              <span className="mono dim small">{e.displayNo}</span>
              {e.storm && <span className="pill warn tiny-pill">storm</span>}
              {e.commissioning && <span className="pill warn tiny-pill">commissioning</span>}
              {e.overflowed && <span className="pill info tiny-pill">overflow</span>}
            </div>
            <h2 id="offer-title">{e.siteName}</h2>
            <div id="offer-desc" className="offer-meta">
              {human(e.primaryAnalytic, 'Alarm')} · {camShort(e.primaryCameraName)}
              {e.companyName ? ` · ${e.companyName}` : ''}
            </div>
            <div className="offer-meta">
              <b className="hot">{plural(e.alarmCount, 'alarm')}</b> on {plural(e.cameraCount, 'camera')} · first alarm <Since from={e.firstAlarmAt} className="mono" /> ago
            </div>
            {e.target?.groupName && <div className="offer-meta">For {e.target.groupName}</div>}
            <QueueAiLine e={e} />
          </div>
        </div>
        {e.transferNote && (
          <div className="offer-note">
            <Icon name="note" />
            <span>{e.transferNote}</span>
          </div>
        )}
        {offer.prestage.cameraIds.length > 0 && (
          <div className="note offer-pre">
            <Icon name="cam" /> Pre-loading {plural(Math.min(4, offer.prestage.cameraIds.length), 'camera')} so video is ready when you accept.
          </div>
        )}
        {error ? (
          <div className="inline-err" role="alert">
            <Icon name="alert" />
            {errorText(error)}
          </div>
        ) : null}
        {declining ? (
          <div className="offer-decline">
            <div className="chips" role="group" aria-label="Decline reason">
              {DECLINE_REASONS.map((r) => (
                <button key={r} type="button" className={`chip${reason === r ? ' hit' : ''}`} aria-pressed={reason === r} onClick={() => setReason(r)}>
                  {r}
                </button>
              ))}
            </div>
            <input ref={reasonRef} value={reason} onChange={(x) => setReason(x.target.value)} placeholder="Reason (optional)" aria-label="Decline reason (optional)" maxLength={200} />
            <div className="offer-act">
              <button type="button" className="btn" onClick={() => setDeclining(false)} disabled={!!busy}>
                Back <kbd>Esc</kbd>
              </button>
              <span className="sp" />
              <button type="button" className="btn badb" onClick={() => onDecline(reason.trim() || undefined)} disabled={!!busy}>
                {busy === 'decline' ? 'Declining…' : 'Decline'} <kbd>Enter</kbd>
              </button>
            </div>
          </div>
        ) : (
          <div className="offer-act">
            <button type="button" className="btn" onClick={() => setDeclining(true)} disabled={!!busy}>
              Decline <kbd>Esc</kbd>
            </button>
            <span className="sp" />
            <button type="button" className="btn pri big" onClick={onAccept} disabled={!!busy || left <= 0}>
              <Icon name="check" />
              {busy === 'accept' ? 'Accepting…' : 'Accept'} <kbd>Enter</kbd>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- the layer ---------- */

export function LiveLayer() {
  const toast = useToaster();
  const presence = usePresence();
  const reloadPresence = presence?.reload;
  const stream = useStreamStatus();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const offerRef = useRef<Offer | null>(null);
  offerRef.current = offer;
  const chimed = useRef<string | null>(null);
  /** The offer I am accepting or declining: its offer_withdrawn is my own doing, not news. */
  const acting = useRef<string | null>(null);

  const show = useCallback((o: Offer | null) => {
    setOffer((cur) => (cur?.offerId === o?.offerId ? cur : o));
    if (!o) return;
    setError(null);
    if (chimed.current !== o.offerId) {
      chimed.current = o.offerId;
      if ((ms(o.expiresAt) ?? 0) > Date.now()) chime('offer');
    }
  }, []);

  const fetchMine = useCallback(() => {
    api.offerMine().then(
      (o) => show(o && (ms(o.expiresAt) ?? 0) > Date.now() ? o : null),
      (e: unknown) => {
        // A Phase 1 server has no offers: stay quiet.
        if (!(e instanceof ApiError && (e.status === 404 || e.status === 405))) console.warn('GET /api/offers/mine failed', e);
      },
    );
  }, [show]);

  // On load, and after every reconnect (an offer may have arrived while the stream was down).
  const wasOpen = useRef(false);
  useEffect(() => {
    const open = stream.state === 'open';
    if (open && !wasOpen.current) fetchMine();
    wasOpen.current = open;
  }, [stream.state, fetchMine]);

  useStreamEvent('offer', (raw) => {
    const o = readOffer(raw);
    if (o) show(o);
  });
  useStreamEvent('offer_withdrawn', (m) => {
    const cur = offerRef.current;
    if (!cur || cur.offerId !== m.offerId || acting.current === m.offerId) return;
    setOffer(null);
    setBusy(null);
  });

  // No withdrawal after expiry (missed message): drop the card and ask the server.
  useEffect(() => {
    if (!offer) return;
    const t = setTimeout(
      () => {
        if (offerRef.current?.offerId !== offer.offerId) return;
        setOffer(null);
        fetchMine();
      },
      Math.max(0, (ms(offer.expiresAt) ?? 0) - Date.now()) + EXPIRY_GRACE_MS,
    );
    return () => clearTimeout(t);
  }, [offer, fetchMine]);

  // A raise chimes and shows in the tabs and queue (no pop-up, 2026-10-28).
  useStreamEvent('raise', (m: RaiseMessage) => {
    chime('raise');
    emitLive(m.cause === 'reassigned' ? { type: 'assigned', eventId: m.eventId } : { type: 'raised', eventId: m.eventId });
    emitLive({ type: 'mine-changed' });
  });

  const accept = async () => {
    const o = offerRef.current;
    if (!o || busy) return;
    setBusy('accept');
    acting.current = o.offerId;
    setError(null);
    try {
      const d = await api.acceptOffer(o.offerId);
      setOffer(null);
      emitLive({ type: 'accepted', detail: d });
      navigate(href.console(d.id));
      reloadPresence?.();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 409 || e.status === 410)) {
        setOffer(null);
        toast({ tone: 'warn', title: 'That offer is no longer available', body: errorText(e) });
        fetchMine();
      } else setError(e);
    } finally {
      setBusy(null);
      acting.current = null;
    }
  };

  const decline = async (reason: string | undefined) => {
    const o = offerRef.current;
    if (!o || busy) return;
    setBusy('decline');
    acting.current = o.offerId;
    setError(null);
    try {
      await api.declineOffer(o.offerId, reason);
      setOffer(null);
      reloadPresence?.();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 409 || e.status === 410)) {
        setOffer(null);
        fetchMine();
      } else setError(e);
    } finally {
      setBusy(null);
      acting.current = null;
    }
  };

  if (!offer) return null;
  return (
    <>
      <OfferCard key={offer.offerId} offer={offer} busy={busy} error={error} onAccept={() => void accept()} onDecline={(r) => void decline(r)} />
      <Prestage key={`pre-${offer.offerId}`} offer={offer} />
    </>
  );
}
